// Refresh CRM from local Claude Code (which has MCP access to Gmail + WhatsApp).
//
// Privacy : Jeremy doesn t want to share email content with the cloud.
// So the flow is :
//   1. CRM generates a structured prompt describing the leads + the JSON output schema
//   2. Prompt is copied to clipboard (or shown for manual paste)
//   3. Jeremy runs the prompt in his local Claude Code (which calls Gmail MCP + WhatsApp MCP)
//   4. Claude returns a JSON blob
//   5. Jeremy pastes it back, CRM applies UPDATEs in bulk to Supabase

import { crmSupabase } from "./crm-client";

export type LeadForRefresh = {
  id: string;
  full_name: string;
  primary_email: string | null;
  primary_phone: string | null;
  is_estrella: boolean;
  status: string;
  last_contact_at: string | null;
  last_response_at: string | null;
  next_actions: { id: string; what: string; status: string }[];
};

export type RefreshUpdate = {
  lead_id: string;
  full_name?: string;
  last_contact_at?: string | null;
  last_response_at?: string | null;
  auto_done_action_ids?: string[];
  new_next_action?: { what: string; due_date: string; priority?: number; ball_in_court?: string } | null;
  snooze_reason?: string | null;
  notes_to_append?: string | null;
};

export type RefreshPayload = {
  refresh_date: string;
  updates: RefreshUpdate[];
  notes?: string;
};

export async function fetchLeadsForRefresh(): Promise<LeadForRefresh[]> {
  if (!crmSupabase) return [];
  const { data, error } = await crmSupabase
    .from("lead")
    .select(
      "id,full_name,is_estrella,status,last_contact_at,last_response_at,identities:lead_identity(kind,value,is_primary),next_actions:next_action(id,what,status)",
    )
    .in("status", ["active", "responded"])
    .eq("is_test", false)
    .order("is_estrella", { ascending: false })
    .order("last_contact_at", { ascending: true, nullsFirst: true });
  if (error) throw error;
  const rows = (data as any[]) ?? [];
  return rows.map((r) => {
    const ids = r.identities ?? [];
    return {
      id: r.id,
      full_name: r.full_name,
      primary_email:
        ids.find((i: any) => i.kind === "email" && i.is_primary)?.value ?? null,
      primary_phone: ids.find((i: any) => i.kind === "phone")?.value ?? null,
      is_estrella: r.is_estrella,
      status: r.status,
      last_contact_at: r.last_contact_at,
      last_response_at: r.last_response_at,
      next_actions: (r.next_actions ?? []).filter((a: any) =>
        ["open", "late"].includes(a.status),
      ),
    };
  });
}

export async function getLastRefreshAt(): Promise<string | null> {
  if (!crmSupabase) return null;
  const { data } = await crmSupabase
    .from("app_setting")
    .select("value")
    .eq("key", "last_refresh_at")
    .maybeSingle();
  return (data?.value as string) ?? null;
}

export async function setLastRefreshAt(iso: string) {
  if (!crmSupabase) return;
  const { data: existing } = await crmSupabase
    .from("app_setting")
    .select("key")
    .eq("key", "last_refresh_at")
    .maybeSingle();
  if (existing) {
    await crmSupabase
      .from("app_setting")
      .update({ value: iso, updated_at: new Date().toISOString() })
      .eq("key", "last_refresh_at");
  } else {
    await crmSupabase.from("app_setting").insert({ key: "last_refresh_at", value: iso });
  }
}

export function buildRefreshPrompt(leads: LeadForRefresh[], lastRefreshAt: string | null): string {
  const since = lastRefreshAt ?? new Date(Date.now() - 7 * 86400_000).toISOString();
  return `Tu es un agent de refresh CRM LLYLL.

CONTEXTE
========
- Tu dois interroger Gmail (4 comptes MCP : gmail-perso, gmail-llyll, gmail-jharroch, gmail-impersonate)
  ET WhatsApp (MCP whatsapp) pour mettre à jour les leads ci-dessous.
- Tu cherches uniquement les emails/messages échangés depuis ${since}.
- Pour chaque lead, identifie :
  • last_contact_at : date du dernier email/WA envoyé PAR Jeremy (ou Ethan via gmail-impersonate) vers ce lead
  • last_response_at : date du dernier email/WA reçu DE ce lead
  • auto_done_action_ids : si le lead a répondu et qu'il avait une next_action 'open'/'late' qui attendait cette réponse, marquer ces actions comme "à clore"
  • snooze_reason : si le lead a répondu très récemment (< 10j), indique-le pour qu'on l'auto-snooze des prochains touchpoints
  • new_next_action : si tu détectes une action évidente à proposer (ex: "préparer le call du 25/05" mentionné dans son mail), propose-la

INSTRUCTIONS
============
1. Pour chaque lead avec primary_email ou primary_phone, fais une recherche ciblée dans les MCP.
2. NE liste PAS les threads dans la réponse (Jeremy ne veut pas les voir ici, juste les dates et les actions).
3. Si tu ne trouves aucune nouvelle info pour un lead, NE l'inclus PAS dans "updates".
4. Si tu vois un nouveau lead avec qui Jeremy a échangé mais qui n'est pas dans la liste, ajoute-le dans "notes" en fin de réponse (Jeremy l'ajoutera manuellement plus tard).

LEADS À REFRESH (${leads.length} actifs)
${"=".repeat(60)}
${JSON.stringify(leads, null, 2)}

DERNIER REFRESH
===============
${since}

FORMAT DE SORTIE
================
Renvoie UNIQUEMENT du JSON valide entre les balises \`\`\`json ... \`\`\` au format :

\`\`\`json
{
  "refresh_date": "${new Date().toISOString()}",
  "updates": [
    {
      "lead_id": "uuid-du-lead",
      "full_name": "Nom (pour debug)",
      "last_contact_at": "2026-05-16T14:30:00Z",
      "last_response_at": "2026-05-17T08:15:00Z",
      "auto_done_action_ids": ["uuid-de-l-action-clos"],
      "new_next_action": { "what": "Préparer le call du 25/05", "due_date": "2026-05-24", "priority": 2, "ball_in_court": "me" },
      "snooze_reason": "a répondu il y a 0 jour"
    }
  ],
  "notes": "Optionnel : leads inconnus détectés, observations, etc."
}
\`\`\`

Démarre maintenant.`;
}

export async function applyRefreshUpdates(payload: RefreshPayload): Promise<{
  leadsUpdated: number;
  actionsClosed: number;
  actionsCreated: number;
  errors: string[];
}> {
  if (!crmSupabase) throw new Error("no supabase");
  const errors: string[] = [];
  let leadsUpdated = 0;
  let actionsClosed = 0;
  let actionsCreated = 0;

  for (const u of payload.updates ?? []) {
    if (!u.lead_id) {
      errors.push("update sans lead_id");
      continue;
    }
    try {
      const patch: Record<string, unknown> = {};
      if (u.last_contact_at !== undefined) patch.last_contact_at = u.last_contact_at;
      if (u.last_response_at !== undefined) {
        patch.last_response_at = u.last_response_at;
        if (u.last_response_at) patch.status = "responded";
      }
      if (Object.keys(patch).length > 0) {
        const { error } = await crmSupabase.from("lead").update(patch).eq("id", u.lead_id);
        if (error) errors.push(`lead ${u.full_name}: ${error.message}`);
        else leadsUpdated += 1;
      }
      if (u.auto_done_action_ids?.length) {
        for (const aid of u.auto_done_action_ids) {
          const { error } = await crmSupabase
            .from("next_action")
            .update({ status: "done", done_at: new Date().toISOString() })
            .eq("id", aid);
          if (error) errors.push(`action ${aid}: ${error.message}`);
          else actionsClosed += 1;
        }
      }
      if (u.new_next_action) {
        const { error } = await crmSupabase.from("next_action").insert({
          lead_id: u.lead_id,
          what: u.new_next_action.what,
          due_date: u.new_next_action.due_date,
          priority: u.new_next_action.priority ?? 3,
          ball_in_court: u.new_next_action.ball_in_court ?? "me",
          status: "open",
          auto_relance_days: 3,
        });
        if (error) errors.push(`new action ${u.full_name}: ${error.message}`);
        else actionsCreated += 1;
      }
    } catch (e: any) {
      errors.push(`${u.full_name}: ${e?.message ?? e}`);
    }
  }

  await setLastRefreshAt(new Date().toISOString());
  return { leadsUpdated, actionsClosed, actionsCreated, errors };
}

/** Extract JSON from a Claude response that might have ```json ... ``` fences */
export function extractJsonFromText(text: string): RefreshPayload {
  let body = text.trim();
  // Try to find ```json ... ``` block
  const fenceMatch = body.match(/```(?:json)?\s*([\s\S]+?)\s*```/);
  if (fenceMatch) body = fenceMatch[1].trim();
  // Otherwise assume the whole thing is JSON
  const parsed = JSON.parse(body);
  if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.updates)) {
    throw new Error("payload invalide : il faut un object avec un tableau 'updates'");
  }
  return parsed as RefreshPayload;
}
